import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Wallet, Clock, CheckCircle2, Loader2, Landmark, Download } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import StatCard from '../../components/StatCard';
import LoadingSpinner from '../../components/LoadingSpinner';
import * as payoutsApi from '../../api/payouts.api';
import { errorMessage } from '../../api/client';
import { formatMoney, formatDateTime } from '../../utils/format';

const STATUS_TONE = {
  PENDING:    'warning',
  APPROVED:   'info',
  PROCESSING: 'brand',
  PAID:       'success',
  FAILED:     'danger',
  REJECTED:   'danger',
  CANCELLED:  'neutral',
};

function MyPayouts() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [list, sum] = await Promise.all([
          payoutsApi.listMyPayouts(statusFilter ? { status: statusFilter } : {}),
          payoutsApi.myPayoutSummary(),
        ]);
        if (cancelled) return;
        setRows(list || []);
        setSummary(sum || null);
      } catch (e) {
        if (!cancelled) setErr(errorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [statusFilter]);

  const totals = summary?.totals || {};
  const counts = summary?.counts || {};
  const owed = useMemo(
    () => (totals.PENDING || 0) + (totals.APPROVED || 0) + (totals.PROCESSING || 0),
    [totals]
  );

  if (loading && !rows.length) return <LoadingSpinner />;

  return (
    <>
      <PageHeader
        title="My payouts"
        subtitle="Money owed to your clinic from completed customer bookings."
        actions={
          <div className="row" style={{ gap: 8 }}>
            <button
              className="btn btn-ghost"
              onClick={async () => {
                try {
                  const blob = await payoutsApi.exportMyPayoutsCsv(statusFilter ? { status: statusFilter } : {});
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `my-payouts-${new Date().toISOString().slice(0, 10)}.csv`;
                  a.click();
                  URL.revokeObjectURL(url);
                } catch (e) { setErr(errorMessage(e)); }
              }}
            >
              <Download size={16} /> Export CSV
            </button>
            <Link to="/clinic/bank-details" className="btn btn-ghost">
              <Landmark size={16} /> Bank details
            </Link>
          </div>
        }
      />

      <div className="grid grid-4" style={{ marginBottom: 'var(--s-4)' }}>
        <StatCard icon={Clock}       tone="warning" label="Pending"     value={counts.PENDING || 0}    deltaLabel={formatMoney(totals.PENDING || 0)} />
        <StatCard icon={Loader2}     tone="brand"   label="Processing"  value={counts.PROCESSING || 0} deltaLabel={formatMoney(totals.PROCESSING || 0)} />
        <StatCard icon={CheckCircle2} tone="success" label="Paid"        value={counts.PAID || 0}       deltaLabel={formatMoney(totals.PAID || 0)} />
        <StatCard icon={Wallet}      tone="brand"   label="Awaiting"    value={formatMoney(owed)}      deltaLabel="Not yet paid" />
      </div>

      <DataTable
        loading={loading}
        rows={rows}
        searchPlaceholder="Search by booking, order…"
        toolbarLeft={
          <select
            className="input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ maxWidth: 200 }}
          >
            <option value="">All statuses</option>
            {['PENDING','APPROVED','PROCESSING','PAID','FAILED','REJECTED','CANCELLED'].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        }
        columns={[
          {
            key: 'order', header: 'Booking',
            accessor: (r) => r.order?.bookingId || r.order?.orderNumber || '',
            render: (r) => (
              <div style={{ minWidth: 0 }}>
                <div className="cell-strong cell-mono">{r.order?.bookingId || '—'}</div>
                <div className="muted text-xs cell-mono">{r.order?.orderNumber}</div>
              </div>
            )
          },
          {
            key: 'customer', header: 'Customer',
            render: (r) => <span>{r.customer?.name || '—'}</span>
          },
          {
            key: 'gross', header: 'Customer paid',
            render: (r) => <span className="muted">{formatMoney(r.grossAmount, r.currency)}</span>
          },
          {
            key: 'pct', header: '%',
            render: (r) => <span className="badge neutral">{r.clinicSharePct}%</span>
          },
          {
            key: 'clinicAmount', header: 'You get',
            render: (r) => <span className="cell-strong">{formatMoney(r.clinicAmount, r.currency)}</span>
          },
          {
            key: 'status', header: 'Status',
            render: (r) => <StatusBadge tone={STATUS_TONE[r.status] || 'neutral'}>{r.status}</StatusBadge>
          },
          {
            key: 'createdAt', header: 'Created',
            render: (r) => <span className="muted">{formatDateTime(r.createdAt)}</span>
          },
          {
            key: 'paidAt', header: 'Paid at',
            render: (r) => <span className="muted">{r.paidAt ? formatDateTime(r.paidAt) : '—'}</span>
          },
          {
            key: 'actions', header: '',
            render: (r) => (
              <Link className="btn btn-ghost btn-sm" to={`/my-payouts/${r._id}`}>View</Link>
            )
          }
        ]}
        emptyTitle="No payouts yet"
        emptyMessage="Payouts appear here once a customer confirms they received the service."
      />

      {err && <div className="notice warning" style={{ marginTop: 'var(--s-4)' }}>{err}</div>}
    </>
  );
}

export default MyPayouts;
